import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  ArrowRight,
  ArrowRightLeft,
  Hash,
  Building2,
  Calendar,
  User,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { ChangeRequestWorkflow } from './ChangeRequestWorkflow';

interface ChangeRequestDetailModalProps {
  requestId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (date: string | Date | null) => {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getRequestTypeIcon = (type: string) => {
  switch (type) {
    case 'roll_in': return <ArrowRight className="h-5 w-5 text-blue-500" />;
    case 'rollout': return <ArrowRightLeft className="h-5 w-5 text-orange-500" />;
    case 'account_number_change': return <Hash className="h-5 w-5 text-purple-500" />;
    case 'site_disconnection': return <Building2 className="h-5 w-5 text-red-500" />;
    default: return <AlertCircle className="h-5 w-5" />;
  }
};

const getRequestTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    roll_in: 'Roll In',
    rollout: 'Rollout',
    account_number_change: 'Account Number Change',
    site_disconnection: 'Site Disconnection',
  };
  return labels[type] || type;
};

const getStatusBadge = (status: string) => {
  const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', icon: typeof Clock }> = {
    pending: { variant: 'secondary', icon: Clock },
    under_review: { variant: 'default', icon: Clock },
    approved: { variant: 'default', icon: CheckCircle },
    rejected: { variant: 'destructive', icon: XCircle },
    executed: { variant: 'default', icon: CheckCircle },
    failed: { variant: 'destructive', icon: XCircle },
    cancelled: { variant: 'secondary', icon: XCircle },
  };

  const config = variants[status] || variants.pending;
  const Icon = config.icon;
  const statusLabel = status.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  return (
    <Badge variant={config.variant} className="gap-1.5">
      <Icon className="h-3 w-3" />
      {statusLabel}
    </Badge>
  );
};

export function ChangeRequestDetailModal({ requestId, isOpen, onClose }: ChangeRequestDetailModalProps) {
  const { data: request, isLoading } = useQuery({
    queryKey: ['change-management-request', requestId],
    queryFn: async () => {
      if (!requestId) return null;
      
      const { data, error } = await supabase
        .from('tum_change_management_requests')
        .select('*')
        .eq('id', requestId)
        .single();
      
      if (!data) return null;
      
      // Fetch user profiles separately if needed
      const userIds = [
        data.requested_by,
        data.reviewed_by,
        data.approved_by,
        data.executed_by,
      ].filter(Boolean) as string[];
      
      let profiles: Record<string, { contact_email?: string }> = {};
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, contact_email')
          .in('user_id', userIds);
        
        profilesData?.forEach(profile => {
          profiles[profile.user_id] = profile;
        });
      }
      
      return {
        ...data,
        requested_by_profile: data.requested_by ? profiles[data.requested_by] : null,
        reviewed_by_profile: data.reviewed_by ? profiles[data.reviewed_by] : null,
        approved_by_profile: data.approved_by ? profiles[data.approved_by] : null,
        executed_by_profile: data.executed_by ? profiles[data.executed_by] : null,
      };

      if (error) throw error;
      return data;
    },
    enabled: !!requestId && isOpen,
  });

  if (!isOpen || !requestId) return null;

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="py-8 text-center text-muted-foreground">
            <Clock className="h-8 w-8 mx-auto mb-2 animate-spin" />
            <p>Loading request details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!request) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="py-8 text-center text-muted-foreground">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>Request not found</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const requestData = request.request_data || {};

  const renderRequestDetails = () => {
    switch (request.request_type) {
      case 'roll_in':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">New Account Number</label>
                <p className="text-sm font-medium">{requestData.new_account_number || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Existing Account ID</label>
                <p className="text-sm font-medium">{requestData.existing_account_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Target Customer ID</label>
                <p className="text-sm font-medium">{requestData.target_customer_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Target Site ID</label>
                <p className="text-sm font-medium">{requestData.target_site_id || 'N/A'}</p>
              </div>
            </div>
            {requestData.mapping_rules && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Mapping Rules</label>
                <pre className="text-xs bg-muted p-3 rounded-md mt-1 overflow-auto">
                  {JSON.stringify(requestData.mapping_rules, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );

      case 'rollout':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Account ID</label>
                <p className="text-sm font-medium">{requestData.account_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Rollout Date</label>
                <p className="text-sm font-medium">{requestData.rollout_date || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Stop Bill Feeding</label>
                <p className="text-sm font-medium">{requestData.stop_bill_feeding ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Archive Bills</label>
                <p className="text-sm font-medium">{requestData.archive_bills ? 'Yes' : 'No'}</p>
              </div>
            </div>
            {requestData.reason && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Reason</label>
                <p className="text-sm">{requestData.reason}</p>
              </div>
            )}
          </div>
        );

      case 'account_number_change':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Account ID</label>
                <p className="text-sm font-medium">{requestData.account_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Effective Date</label>
                <p className="text-sm font-medium">{requestData.effective_date || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Old Account Number</label>
                <p className="text-sm font-medium">{requestData.old_account_number || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">New Account Number</label>
                <p className="text-sm font-medium">{requestData.new_account_number || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Preserve History</label>
                <p className="text-sm font-medium">{requestData.preserve_history ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </div>
        );

      case 'site_disconnection':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Site ID</label>
                <p className="text-sm font-medium">{requestData.site_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Disconnection Date</label>
                <p className="text-sm font-medium">{requestData.disconnection_date || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Handle Accounts</label>
                <p className="text-sm font-medium">{requestData.handle_accounts || 'N/A'}</p>
              </div>
              {requestData.transfer_to_site_id && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Transfer To Site ID</label>
                  <p className="text-sm font-medium">{requestData.transfer_to_site_id}</p>
                </div>
              )}
            </div>
            {requestData.reason && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Reason</label>
                <p className="text-sm">{requestData.reason}</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto">
              {JSON.stringify(requestData, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getRequestTypeIcon(request.request_type)}
            {getRequestTypeLabel(request.request_type)} Request
          </DialogTitle>
          <DialogDescription>
            Request ID: {request.id.slice(0, 8)}...
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="workflow">Workflow</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Request Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <div className="mt-1">{getStatusBadge(request.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Priority</label>
                    <div className="mt-1">
                      <Badge variant={request.priority === 'urgent' ? 'destructive' : request.priority === 'high' ? 'default' : 'secondary'}>
                        {request.priority}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Requested By</label>
                    <p className="text-sm mt-1">
                      {request.requested_by_profile?.contact_email || 
                       `User ${request.requested_by?.slice(0, 8) || 'N/A'}`}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created At</label>
                    <p className="text-sm mt-1">{formatDate(request.created_at)}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Timeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {request.reviewed_at && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Reviewed At</label>
                      <p className="text-sm mt-1">{formatDate(request.reviewed_at)}</p>
                      {request.reviewed_by_profile && (
                        <p className="text-xs text-muted-foreground mt-1">
                          by {request.reviewed_by_profile.contact_email}
                        </p>
                      )}
                    </div>
                  )}
                  {request.approved_at && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Approved At</label>
                      <p className="text-sm mt-1">{formatDate(request.approved_at)}</p>
                      {request.approved_by_profile && (
                        <p className="text-xs text-muted-foreground mt-1">
                          by {request.approved_by_profile.contact_email}
                        </p>
                      )}
                    </div>
                  )}
                  {request.executed_at && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Executed At</label>
                      <p className="text-sm mt-1">{formatDate(request.executed_at)}</p>
                      {request.executed_by_profile && (
                        <p className="text-xs text-muted-foreground mt-1">
                          by {request.executed_by_profile.contact_email}
                        </p>
                      )}
                    </div>
                  )}
                  {request.requested_effective_date && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Effective Date</label>
                      <p className="text-sm mt-1">{formatDate(request.requested_effective_date)}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {request.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{request.description}</p>
                </CardContent>
              </Card>
            )}

            {request.rejection_reason && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base text-red-600">Rejection Reason</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-red-600">{request.rejection_reason}</p>
                </CardContent>
              </Card>
            )}

            {request.execution_error && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base text-red-600">Execution Error</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-red-600">{request.execution_error}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="workflow" className="space-y-4">
            <ChangeRequestWorkflow
              status={request.status}
              requestedAt={request.created_at}
              reviewedAt={request.reviewed_at}
              approvedAt={request.approved_at}
              executedAt={request.executed_at}
              rejectedAt={request.status === 'rejected' ? request.reviewed_at : null}
              rejectionReason={request.rejection_reason}
            />
          </TabsContent>

          <TabsContent value="details" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Request Details</CardTitle>
              </CardHeader>
              <CardContent>
                {renderRequestDetails()}
              </CardContent>
            </Card>

            {(request.admin_notes || request.execution_notes) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {request.admin_notes && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Admin Notes</label>
                      <p className="text-sm mt-1">{request.admin_notes}</p>
                    </div>
                  )}
                  {request.execution_notes && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Execution Notes</label>
                      <p className="text-sm mt-1">{request.execution_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

